import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Eye, Download, Send, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [downloading, setDownloading] = useState({});

  const fetchData = () => api.get('/client-invoices').then(r => setInvoices(r.data)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { fetchData(); }, []);

  const handleStatusChange = async (id, status) => {
    await api.put(`/client-invoices/${id}`, { status });
    fetchData();
  };

  const downloadPdf = async (id, invoiceNumber) => {
    setDownloading(p => ({ ...p, [id]: true }));
    try {
      const response = await api.get(`/client-invoices/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoiceNumber || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error('PDF download failed:', e); }
    finally { setDownloading(p => ({ ...p, [id]: false })); }
  };

  const sendInvoice = async (id) => {
    await api.put(`/client-invoices/${id}`, { status: 'sent' });
    fetchData();
  };

  const viewInvoice = async (id) => {
    const { data } = await api.get(`/client-invoices/${id}`);
    setSelectedInvoice(data);
    setDetailOpen(true);
  };

  const statusColors = { draft: 'bg-slate-100 text-slate-600', sent: 'bg-blue-50 text-blue-700 border-blue-200', paid: 'bg-emerald-50 text-emerald-700 border-emerald-200', overdue: 'bg-red-50 text-red-700 border-red-200' };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;

  return (
    <div data-testid="invoices-page">
      <div className="page-header">
        <h1 className="page-title">Client Invoices</h1>
      </div>

      <div className="table-wrap">
        <table className="w-full data-table min-w-[800px]">
          <thead><tr><th>Invoice #</th><th>Owner</th><th>Period</th><th>Revenue</th><th>Fees</th><th>Net Payout</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {invoices.map(inv => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                <td className="font-medium text-slate-900">{inv.invoice_number}</td>
                <td>{inv.owner_name}</td>
                <td className="text-sm">
                  {inv.period_start ? format(new Date(inv.period_start), 'MMM dd') : '?'} - {inv.period_end ? format(new Date(inv.period_end), 'MMM dd, yyyy') : '?'}
                </td>
                <td>R{(inv.gross_revenue || 0).toLocaleString()}</td>
                <td>R{(inv.total_fees || 0).toLocaleString()}</td>
                <td className="font-medium text-emerald-700">R{(inv.net_payout || 0).toLocaleString()}</td>
                <td>
                  <Select value={inv.status} onValueChange={v => handleStatusChange(inv.id, v)}>
                    <SelectTrigger className="h-7 text-xs w-24"><Badge className={statusColors[inv.status] || ''}>{inv.status}</Badge></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => viewInvoice(inv.id)} data-testid={`view-invoice-${inv.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => downloadPdf(inv.id, inv.invoice_number)} disabled={downloading[inv.id]} data-testid={`download-invoice-${inv.id}`}>
                      {downloading[inv.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    </Button>
                    {inv.status === 'draft' && (
                      <Button variant="ghost" size="sm" onClick={() => sendInvoice(inv.id)} className="text-blue-600 hover:bg-blue-50" data-testid={`send-invoice-${inv.id}`}><Send className="w-3.5 h-3.5" /></Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {invoices.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">No invoices</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Invoice {selectedInvoice?.invoice_number}</DialogTitle></DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Owner:</span> <span className="font-medium">{selectedInvoice.owner_name}</span></div>
                <div><span className="text-slate-500">Email:</span> <span>{selectedInvoice.owner_email}</span></div>
                <div><span className="text-slate-500">Invoice Date:</span> <span>{selectedInvoice.invoice_date ? format(new Date(selectedInvoice.invoice_date), 'MMM dd, yyyy') : 'N/A'}</span></div>
                <div><span className="text-slate-500">Due Date:</span> <span>{selectedInvoice.due_date ? format(new Date(selectedInvoice.due_date), 'MMM dd, yyyy') : 'N/A'}</span></div>
              </div>
              <div className="border-t border-slate-200 pt-4">
                <h4 className="text-sm font-semibold mb-3">Fee Breakdown</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>Gross Revenue</span><span className="font-medium">R{(selectedInvoice.gross_revenue || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between text-slate-500"><span>Booking Fees</span><span>-R{(selectedInvoice.booking_fees || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between text-slate-500"><span>Cleaning Fees</span><span>-R{(selectedInvoice.cleaning_fees || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between text-slate-500"><span>Marketing Fee</span><span>-R{(selectedInvoice.marketing_fee || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between text-slate-500"><span>Other Fees</span><span>-R{(selectedInvoice.other_fees || 0).toLocaleString()}</span></div>
                  {selectedInvoice.vat_amount > 0 && <div className="flex justify-between text-slate-500"><span>VAT ({selectedInvoice.vat_percentage}%)</span><span>-R{(selectedInvoice.vat_amount || 0).toLocaleString()}</span></div>}
                  <div className="flex justify-between border-t pt-2 font-semibold text-emerald-700"><span>Net Payout</span><span>R{(selectedInvoice.net_payout || 0).toLocaleString()}</span></div>
                </div>
              </div>
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <Button onClick={() => downloadPdf(selectedInvoice.id, selectedInvoice.invoice_number)} className="bg-slate-900 hover:bg-slate-800" disabled={downloading[selectedInvoice.id]} data-testid="invoice-detail-download-btn">
                  {downloading[selectedInvoice.id] ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                  Download PDF
                </Button>
                {selectedInvoice.status === 'draft' && (
                  <Button variant="outline" onClick={() => { sendInvoice(selectedInvoice.id); setDetailOpen(false); }} data-testid="invoice-detail-send-btn">
                    <Send className="w-4 h-4 mr-2" />Mark as Sent
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
